import { useState, Fragment, type CSSProperties } from "react";
import { Card, EmptyState, Avatar, Pill, ConfirmModal, showToast } from "../components/primitives";
import { InviteMemberModal } from "../components/members/InviteMemberModal";
import {
  useWorkspaceMembers,
  useUpdateMemberRole,
  useResendInvitation,
  useCancelInvitation,
  type PendingInvitation,
} from "../hooks/use-workspace-members";
import { useAuth } from "../../lib/hooks/use-auth";
import { useMyAccess } from "../hooks/use-node-access";

/** Relative label that also reads naturally for future timestamps (expiry). */
function relativeTime(iso: string): string {
  const diffMs = new Date(iso).getTime() - Date.now();
  const future = diffMs > 0;
  const mins = Math.round(Math.abs(diffMs) / 60_000);
  let label: string;
  if (mins < 1) label = "just now";
  else if (mins < 60) label = `${mins}m`;
  else if (mins < 60 * 24) label = `${Math.round(mins / 60)}h`;
  else label = `${Math.round(mins / (60 * 24))}d`;
  if (label === "just now") return label;
  return future ? `in ${label}` : `${label} ago`;
}

export function MembersPage() {
  const members = useWorkspaceMembers();
  const { user, currentWorkspaceId } = useAuth();
  const updateRole = useUpdateMemberRole();
  const resend = useResendInvitation();
  const cancel = useCancelInvitation();

  const [inviteOpen, setInviteOpen] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [cancelTarget, setCancelTarget] = useState<PendingInvitation | null>(null);
  // Sprint 14c (S14C.34): per-member "what can they see" expansion.
  const [expandedAccess, setExpandedAccess] = useState<Set<string>>(new Set());

  const myUserId = user?.id;
  const myRole = user?.workspaces.find((w) => w.id === currentWorkspaceId)?.role;
  const isOwner = myRole === "owner";

  const header: CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 20,
  };

  const table: CSSProperties = {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 13,
  };

  const th: CSSProperties = {
    textAlign: "left",
    fontSize: 11,
    fontWeight: 500,
    color: "var(--color-text-tertiary)",
    padding: "8px 12px",
    borderBottom: "var(--kuvio-border-default)",
  };

  const td: CSSProperties = {
    padding: "10px 12px",
    borderBottom: "var(--kuvio-border-default)",
    color: "var(--color-text-primary)",
    verticalAlign: "middle",
  };

  if (members.isLoading) {
    return (
      <div style={{ maxWidth: 720 }}>
        <div style={header}>
          <h1 style={{ fontSize: 22, fontWeight: 500, margin: 0 }}>Members</h1>
        </div>
        <Card>
          <div style={{ height: 120, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ fontSize: 13, color: "var(--color-text-tertiary)" }}>Loading members...</span>
          </div>
        </Card>
      </div>
    );
  }

  if (members.error) {
    return (
      <div style={{ maxWidth: 720 }}>
        <div style={header}>
          <h1 style={{ fontSize: 22, fontWeight: 500, margin: 0 }}>Members</h1>
        </div>
        <Card>
          <EmptyState>Failed to load members</EmptyState>
          <div style={{ textAlign: "center", marginTop: 8 }}>
            <button
              onClick={() => members.refetch()}
              style={{ fontSize: 13, color: "var(--color-text-info)", background: "none", border: "none", cursor: "pointer" }}
            >
              Retry
            </button>
          </div>
        </Card>
      </div>
    );
  }

  const memberList = members.data?.members ?? [];
  const pending = members.data?.pending_invitations ?? [];
  const ownerCount = memberList.filter((m) => m.role === "owner").length;

  function handleRoleChange(userId: string, newRole: "owner" | "member") {
    updateRole.mutate(
      { user_id: userId, new_role: newRole },
      {
        onError: (e) => showToast(e instanceof Error ? e.message : "Couldn't change role", "error", true),
      },
    );
  }

  function handleResend(inv: PendingInvitation) {
    setResendingId(inv.id);
    resend.mutate(inv.id, {
      onSuccess: () => showToast(`Invitation resent to ${inv.email}`),
      onError: (e) => showToast(e instanceof Error ? e.message : "Couldn't resend invitation", "error", true),
      onSettled: () => setResendingId(null),
    });
  }

  const roleSelectStyle: CSSProperties = {
    fontSize: 12,
    padding: "4px 8px",
    border: "var(--kuvio-border-default)",
    borderRadius: "var(--border-radius-sm)",
    background: "var(--color-background-primary)",
    color: "var(--color-text-primary)",
    fontFamily: "var(--font-sans)",
    cursor: "pointer",
  };

  const rowBtn: CSSProperties = {
    fontSize: 12,
    padding: "4px 10px",
    border: "0.5px solid var(--color-border-secondary)",
    borderRadius: "var(--border-radius-sm)",
    background: "transparent",
    color: "var(--color-text-secondary)",
    cursor: "pointer",
    fontFamily: "var(--font-sans)",
  };

  return (
    <div style={{ maxWidth: 720 }}>
      <div style={header}>
        <h1 style={{ fontSize: 22, fontWeight: 500, margin: 0 }}>Members</h1>
        <button
          data-variant="primary"
          onClick={() => setInviteOpen(true)}
          style={{ fontSize: 13, padding: "8px 14px", display: "flex", alignItems: "center", gap: 6 }}
        >
          <i className="ti ti-plus" style={{ fontSize: 14 }} aria-hidden="true" />
          Invite member
        </button>
      </div>

      {successMsg && (
        <div
          style={{
            fontSize: 13,
            color: "var(--c-teal-700)",
            background: "var(--c-teal-50)",
            border: "1px solid var(--c-teal-200)",
            borderRadius: "var(--border-radius-sm)",
            padding: "8px 12px",
            marginBottom: 16,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span>{successMsg}</span>
          <button
            onClick={() => setSuccessMsg(null)}
            style={{ background: "none", border: "none", cursor: "pointer", color: "var(--c-teal-700)", fontSize: 16, lineHeight: 1 }}
            aria-label="Dismiss"
          >
            &times;
          </button>
        </div>
      )}

      <Card>
        {memberList.length === 0 ? (
          <EmptyState>No members yet</EmptyState>
        ) : (
          <table style={table}>
            <thead>
              <tr>
                <th style={th}>Member</th>
                <th style={th}>Role</th>
                <th style={th}>Status</th>
                <th style={th}>Joined</th>
                {isOwner && <th style={th}>Visibility</th>}
              </tr>
            </thead>
            <tbody>
              {memberList.map((member) => {
                const isSelf = member.id === myUserId;
                const isLastOwner = member.role === "owner" && ownerCount <= 1;
                // Owners can change roles, except their own row (no self-demotion
                // via the UI) and the last remaining owner (mirrors the server
                // safeguard). Everyone else sees plain text.
                const canEditRole = isOwner && !isSelf && !isLastOwner;
                return (
                  <Fragment key={member.id}>
                  <tr>
                    <td style={td}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <Avatar persona="gray" size="sm" name={member.name || member.email} />
                        <div>
                          <div style={{ fontWeight: 500 }}>
                            {member.name || member.email}
                            {isSelf && <span style={{ fontWeight: 400, color: "var(--color-text-tertiary)" }}> (You)</span>}
                          </div>
                          {member.name && (
                            <div style={{ fontSize: 11, color: "var(--color-text-tertiary)" }}>{member.email}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td style={td}>
                      {canEditRole ? (
                        <select
                          style={roleSelectStyle}
                          value={member.role}
                          disabled={updateRole.isPending}
                          onChange={(e) => handleRoleChange(member.id, e.target.value as "owner" | "member")}
                          aria-label={`Role for ${member.name || member.email}`}
                        >
                          <option value="owner">owner</option>
                          <option value="member">member</option>
                        </select>
                      ) : (
                        <span style={{ fontSize: 12 }} title={isLastOwner ? "The last owner can't be demoted" : isSelf ? "You can't change your own role" : undefined}>
                          {member.role}
                        </span>
                      )}
                    </td>
                    <td style={td}>
                      <Pill variant="teal">active</Pill>
                    </td>
                    <td style={{ ...td, fontSize: 12, color: "var(--color-text-tertiary)" }}>
                      {member.joined_at ? new Date(member.joined_at).toLocaleDateString() : "—"}
                    </td>
                    {isOwner && (
                      <td style={td}>
                        <button
                          style={rowBtn}
                          onClick={() =>
                            setExpandedAccess((prev) => {
                              const next = new Set(prev);
                              if (next.has(member.id)) next.delete(member.id);
                              else next.add(member.id);
                              return next;
                            })
                          }
                        >
                          <i
                            className={`ti ti-chevron-${expandedAccess.has(member.id) ? "down" : "right"}`}
                            style={{ fontSize: 12, marginRight: 4 }}
                            aria-hidden="true"
                          />
                          What they see
                        </button>
                      </td>
                    )}
                  </tr>
                  {isOwner && expandedAccess.has(member.id) && (
                    <tr>
                      <td colSpan={5} style={{ ...td, background: "var(--color-background-secondary)" }}>
                        <MemberVisibility userId={member.id} name={member.name || member.email} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                  );
                })}
              </tbody>
            </table>
        )}
      </Card>

      {pending.length > 0 && (
        <div style={{ marginTop: 28 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: "var(--color-text-secondary)", marginBottom: 10 }}>
            Pending invitations · {pending.length}
          </div>
          <Card>
            <table style={table}>
              <thead>
                <tr>
                  <th style={th}>Email</th>
                  <th style={th}>Role</th>
                  <th style={th}>Invited by</th>
                  <th style={th}>Invited</th>
                  <th style={th}>Expires</th>
                  {isOwner && <th style={{ ...th, textAlign: "right" }}>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {pending.map((inv) => {
                  const inviterName = memberList.find((m) => m.id === inv.invited_by)?.name;
                  return (
                    <tr key={inv.id}>
                      <td style={td}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <Avatar persona="amber" size="sm" name={inv.email} />
                          <span style={{ fontWeight: 500 }}>{inv.email}</span>
                        </div>
                      </td>
                      <td style={td}>
                        <span style={{ fontSize: 12 }}>{inv.role}</span>
                      </td>
                      <td style={{ ...td, fontSize: 12, color: "var(--color-text-tertiary)" }}>
                        {inviterName ?? "—"}
                      </td>
                      <td style={{ ...td, fontSize: 12, color: "var(--color-text-tertiary)" }}>
                        {relativeTime(inv.created_at)}
                      </td>
                      <td style={{ ...td, fontSize: 12, color: "var(--color-text-tertiary)" }}>
                        {relativeTime(inv.expires_at)}
                      </td>
                      {isOwner && (
                        <td style={{ ...td, textAlign: "right" }}>
                          <div style={{ display: "inline-flex", gap: 6, justifyContent: "flex-end" }}>
                            <button
                              style={rowBtn}
                              disabled={resendingId === inv.id}
                              onClick={() => handleResend(inv)}
                            >
                              {resendingId === inv.id ? "Resending…" : "Resend"}
                            </button>
                            <button
                              style={{ ...rowBtn, color: "var(--c-red-600)" }}
                              onClick={() => setCancelTarget(inv)}
                            >
                              Cancel
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>
        </div>
      )}

      <InviteMemberModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onSuccess={(email) => setSuccessMsg(`Invitation sent to ${email}`)}
      />

      <ConfirmModal
        open={!!cancelTarget}
        onOpenChange={(o) => { if (!o) setCancelTarget(null); }}
        title="Cancel invitation?"
        message={cancelTarget ? `The invitation to ${cancelTarget.email} will be revoked. Their accept link will stop working.` : ""}
        confirmLabel="Cancel invitation"
        cancelLabel="Keep"
        variant="danger"
        onConfirm={async () => {
          if (!cancelTarget) return;
          const email = cancelTarget.email;
          await cancel.mutateAsync(cancelTarget.id);
          setCancelTarget(null);
          showToast(`Invitation to ${email} cancelled`);
        }}
      />
    </div>
  );
}

/**
 * Sprint 14c (S14C.34) — "What can {name} see?" Summarises a member's visibility
 * via `list_my_access`: how many restricted nodes they can see and how many are
 * hidden from them, by type. Mounts only when its row is expanded so the audit
 * query fires on demand.
 */
function MemberVisibility({ userId, name }: { userId: string; name: string }) {
  const { data, isLoading } = useMyAccess(userId);

  if (isLoading) {
    return <span style={{ fontSize: 12, color: "var(--color-text-tertiary)" }}>Loading visibility…</span>;
  }
  if (!data) {
    return <span style={{ fontSize: 12, color: "var(--color-text-tertiary)" }}>Couldn't load visibility.</span>;
  }
  if (data.is_owner) {
    return (
      <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
        {name} is an owner — sees everything. No content is restricted from them.
      </span>
    );
  }

  const hiddenTypes = Object.entries(data.hidden_by_type ?? {});
  return (
    <div style={{ fontSize: 12, color: "var(--color-text-secondary)", lineHeight: 1.6 }}>
      <div>
        <strong style={{ fontWeight: 500 }}>{name}</strong> can see{" "}
        <strong style={{ fontWeight: 500 }}>{data.restricted_visible.length}</strong> restricted item
        {data.restricted_visible.length === 1 ? "" : "s"};{" "}
        <strong style={{ fontWeight: 500 }}>{data.restricted_hidden_count}</strong> restricted item
        {data.restricted_hidden_count === 1 ? " is" : "s are"} hidden from them.
      </div>
      {hiddenTypes.length > 0 && (
        <div style={{ marginTop: 4, color: "var(--color-text-tertiary)" }}>
          Hidden by type:{" "}
          {hiddenTypes.map(([type, count], i) => (
            <span key={type}>
              {i > 0 ? ", " : ""}
              {count} {type}
              {count === 1 ? "" : "s"}
            </span>
          ))}
        </div>
      )}
      {data.restricted_hidden_count === 0 && data.restricted_visible.length === 0 && (
        <div style={{ marginTop: 2, color: "var(--color-text-tertiary)" }}>
          Nothing is restricted in this workspace yet.
        </div>
      )}
    </div>
  );
}
